import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  Hidden,
  TextField
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import ReCAPTCHA from "react-google-recaptcha"
import { useDispatch } from 'react-redux'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { toast } from '../features/snackbar/snackbarSlice'
import Logo from '../Logo'
import { useGetUserInfoQuery, useSignupMutation } from '../services/api'
import { MemberType } from '../types'
import { useRedirectUrl } from '../useRedirectUrl'
import ConfirmPassword from './ConfirmPassword'

const DEFAULT_MEMBER_TYPE: MemberType = process.env.REACT_APP_DEFAULT_MEMBER_TYPE as MemberType
const Signup = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const redirectUrl = useRedirectUrl()
  const [email, setEmail] = useState<string>('')
  const [type, setType] = useState<'practitioner' | MemberType>(DEFAULT_MEMBER_TYPE)
  const [fullName, setFullName] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [username, setUsername] = useState<string>('')
  const [agreeToc, setAgreeToc] = useState<boolean>(false)
  const { data: userInfo, } = useGetUserInfoQuery()
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const dispatch = useDispatch()
  const [
    signup,
    { error, isLoading },
  ] = useSignupMutation()

  useEffect(() => {
    recaptchaRef.current?.reset();
  }, [])

  useEffect(() => {
    if (~location.search.indexOf('practitioner')) {
      setType('practitioner')
    }
  }, [location.search])

  useEffect(() => {
    if (userInfo?.guid) {
      if (type !== 'public' && !userInfo?.verified)
        navigate('/verify')
      else
        navigate(redirectUrl ?? '/dashboard')
    }
  }, [location.search, navigate, redirectUrl, type, userInfo])

  const disabled = !agreeToc
    || !email
    || !fullName
    || isLoading
    || !password
    || !username;

  return (
    <Grid
      container
      spacing={0}
      direction="column"
      alignItems="center"
      justifyContent="center"
      style={{
        minHeight: '100vh',
        paddingBottom: 10,
        paddingTop: 10,
      }}
    >
      <Logo height={128} />
      <br />
      <Grid
        item
        style={{ textAlign: 'center' }}
        xs={3}
      >
        {type === 'practitioner'
          ? <>
            You are signing up as a practitioner.
            <br />
          </>
          : <>
            Please create an account to sign up
            <Hidden smUp><br /></Hidden>
            <Hidden xsDown>&nbsp;</Hidden>
            for our free pilot program.
            <br />
            No credit card needed!
          </>}

      </Grid>
      <br />
      <br />
      <Grid
        item
        xs={3}
      >
        <Box
          component="form"
          display='inline-grid'
          onSubmit={(e: any) => {
            e.preventDefault()
            const captcha = recaptchaRef.current?.getValue() || undefined;
            if (!captcha) {
              dispatch(
                toast({
                  message: 'Please verify that you are not a robot',
                  severity: 'error'
                })
              )
              return
            }

            signup({
              captcha,
              email,
              type,
              fullName,
              password,
              username,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            })
              .unwrap()
              .then(() => {
                navigate('/login')
              })
              .catch(err => {
                console.error(err)
              })
          }}
          sx={{
            '& .MuiTextField-root': {
              mt: 1,
              minWidth: '25ch'
            },
            justifyItems: 'center'
          }}
          autoComplete="off"
        >
          <TextField
            autoFocus
            fullWidth
            label="Full Name"
            onChange={e => setFullName(e.target.value)}
            required
            value={fullName}
          />
          <TextField
            error={!!(error as any)?.data?.username}
            fullWidth
            helperText={(error as any)?.data?.username}
            label="Username"
            onChange={e => setUsername(e.target.value)}
            required
            value={username}
          />
          <TextField
            error={!!(error as any)?.data?.email}
            fullWidth
            helperText={(error as any)?.data?.email}
            label="Email"
            onChange={e => setEmail(e.target.value)}
            required
            value={email}
          />
          <ConfirmPassword
            onChange={setPassword}
          />
          <br />
          <FormControl style={{ maxWidth: 300 }}>
            <FormControlLabel
              control={<Checkbox
                onChange={(_, checked) => setAgreeToc(checked)}
              />}
              label={<>By clicking this checkbox and selecting "Proceed", you acknowledge that you have read and agree to the <a
                href="https://www.sessionsfirst.com/TermsAndConditions"
                rel="noreferrer"
                target="_blank"
              >Terms and Conditions</a> of the site.</>}
            />
          </FormControl>
          <br />
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY!}
          />
          <br />
          <Button
            disabled={disabled}
            fullWidth
            type='submit'
            variant="contained"
          >{isLoading
            ? <CircularProgress
              size={25}
            />
            : 'Signup'
            }</Button>
          <br />
          <Link
            to={`/login${!redirectUrl ? '' : `?redirectUrl=${encodeURIComponent(redirectUrl)}`}`}
          >Back to Login</Link>
        </Box>
      </Grid>
    </Grid>
  )
}

export default Signup